import React, { useState, useEffect } from 'react';
import { 
Container, 
Typography, 
Button, 
Paper, 
TextField,
Dialog,
DialogTitle,
DialogContent,
DialogActions,
IconButton,
Box,
Chip,
Divider,
useTheme,
Grid,
Slide,
MenuItem,
InputLabel,
FormControl,
Select,
TablePagination,
Menu,
TableContainer,
Table,
TableHead,
TableRow,
TableCell,
TableBody
} from '@mui/material';
import { 
Delete, 
Business, 
PictureAsPdf,
TableChart, 
GetApp
} from '@mui/icons-material';

import {
Close as CloseIcon,
Add as AddIcon,
Search as SearchIcon,
FilterList as FilterIcon,
Refresh as RefreshIcon,
Today as TodayIcon,
Edit as EditIcon,
Visibility as ViewIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../../hooks/useToast';
import { TEXT_CONNECTION_ERROR, TEXT_DATA_LOADED } from '../../../services/Text';
import { 
    formatCreatedAtDate, 
    formatNumberWithComma, 
    formatNumberWithComma2, 
    getCategoriesNameById, 
    getStatusById, 
    getUserDetails, 
    recordsPerPage, 
    statusData 
} from '../../../utils/helpers';
import { 
    exportDepartmentsToCSV, 
    exportDepartmentsToExcel, 
    exportDepartmentsToPDFEnhanced 
} from '../../../services/DepartmentService';
import { getUserRole, PERMISSIONS, ROLE_PERMISSIONS } from '../../../utils/Permission';
import { departmentApiService } from '../../../apiServices/DepartmentApiService';
import { branchApiService } from '../../../apiServices/BranchApiService';

const Department = () => {
const theme = useTheme();
const [loading, setLoading] = useState(false);
const [error, setError] = useState(null);
const showToast = useCustomToast();
const [updateId, setUpdateId] = useState('');
const [postStatus, setPostStatus] = useState('ADD');
const [exportAnchorEl, setExportAnchorEl] = useState(null);

const [pageState, setPageState] = useState({
    pageData: [],
});
const [page, setPage] = useState(0);
const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
const [selectedRecord, setSelectedRecord] = useState(null);
const [searchTerm, setSearchTerm] = useState('');
const [formData, setFormData] = useState({
    name: '',
    additional_info: '',
    status: '1'
});
const [filters, setFilters] = useState({
    name: '',
    dateFrom: '',
    dateTo: ''
});
const [errors, setErrors] = useState({
    name: false,
});
// State for dialogs
const [filterOptions, setFilterOptions] = useState(false);
const [openAdd, setOpenAdd] = useState(false);
const [openView, setOpenView] = useState(false);
const [openDelete, setOpenDelete] = useState(false);
const [branchNamesFilterData, setBranchNamesFilterData] = useState([]);
const [selectedBranch, setSelectedBranch] = useState('');
const [branchSelectionOpen, setBranchSelectionOpen] = useState(false);

// Permissions
const [userRole, setUserRole] = useState('');
useEffect(() => {
    const role = getUserRole();
    setUserRole(role);
}, []);

// Helper function
const hasPermission = (permission) => {
    return ROLE_PERMISSIONS[userRole]?.includes(permission) || false;
};

useEffect(() => {
    departmentApiService.setToastFunction(showToast);
    branchApiService.setToastFunction(showToast);
}, [showToast]);

useEffect(() => {
    fetchData();
    fetchBranchNames();
}, []);

const fetchBranchNames = async () => {
    try {
    const response = await branchApiService.listNames();
    if (response.success) {
        setBranchNamesFilterData(response.data || []);
    } else {
        showToast("Error", response.message || "Failed to load branch names", "error");
    }
    } catch (error) {
    console.error("Error fetching branch names:", error);
    showToast("Error", "Failed to load branch names", "error");
    }
};

const fetchData = async (branchId = null) => {
    setLoading(true);
    setError(null);

    try {
    // For super admin, use selected branch or current user's branch
    let branchIdToUse = branchId;
    if (!hasPermission(PERMISSIONS.BRANCHES)) {
        branchIdToUse = getUserDetails("branchId");
    }

    const response = await departmentApiService.list(branchIdToUse);

    if (response.success) {
        const departments = Array.isArray(response.data) ? response.data.reverse() : [];

        setPageState(prev => ({
            ...prev,
            pageData: departments,
        }));

        showToast("Success", TEXT_DATA_LOADED, "success");
    } else {
        setError(response.message);
        showToast("Error", response.message, "error");
    }
    } catch (error) {
    let errorMessage = TEXT_CONNECTION_ERROR;
    setError(errorMessage);
    showToast("Error", errorMessage, "error");
    } finally {
    setLoading(false);
    }
};

const handleBranchSelect = (branchId) => {
    setSelectedBranch(branchId);
    setBranchSelectionOpen(false);
    fetchData(branchId);
};

// Filter departments based on search term and filters
const filteredData = pageState.pageData.filter(department => {
    const matchesSearch = searchTerm === '' ||
    department.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (department.additional_info && department.additional_info.toLowerCase().includes(searchTerm.toLowerCase()));

    // Name filter
    const matchesName = !filters.name || 
    department.name.toLowerCase().includes(filters.name.toLowerCase());

    // Date range filter
    let matchesDate = true;
    if (filters.dateFrom) {
    const recordDate = new Date(department.created_at).toISOString().split('T')[0];
    const filterFromDate = new Date(filters.dateFrom).toISOString().split('T')[0];
    matchesDate = matchesDate && recordDate >= filterFromDate;
    }

    if (filters.dateTo) {
    const recordDate = new Date(department.created_at).toISOString().split('T')[0];
    const filterToDate = new Date(filters.dateTo).toISOString().split('T')[0];
    matchesDate = matchesDate && recordDate <= filterToDate;
    }

    return matchesSearch && matchesName && matchesDate;
});

// Pagination handlers
const handleChangePage = (event, newPage) => {
    setPage(newPage);
};

const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
};

// Dialog handlers
const handlefilterOptions = () => setFilterOptions(true);
const handleCloseOptions = () => setFilterOptions(false);

const handleOpenAdd = () => {
    setPostStatus("ADD");
    setFormData({
    name: '', 
    additional_info: '',
    status: '1',
    });
    setOpenAdd(true);
};

const handleCloseAdd = () => { setOpenAdd(false); };

const handleOpenView = (record) => {
    setSelectedRecord(record);
    setOpenView(true);
};

const handleCloseView = () => setOpenView(false);

const handleOpenEdit = (record) => {
    setSelectedRecord(record);
    setUpdateId(record.id);
    setPostStatus("UPDATE");
    setFormData({
    name: record.name,
    additional_info: record.additional_info || '',
    status: record.status,
    });
    setOpenAdd(true);
};

const handleOpenDelete = (record) => {
    setSelectedRecord(record);
    setUpdateId(record.id);
    setPostStatus("DELETE");
    setOpenDelete(true);
};

const handleCloseDelete = () => {
    setOpenDelete(false);
};

// Form handlers
const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
    ...prev,
    [name]: value
    }));
};

// Filter handlers
const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
    ...prev,
    [name]: value
    }));
};

const applyTodayFilter = () => {
    const today = new Date().toISOString().split('T')[0];
    setFilters({
    ...filters,
    dateFrom: today,
    dateTo: today
    });
    handleCloseOptions();
};

const clearFilters = () => {
    setFilters({
    name: '',
    dateFrom: '',
    dateTo: ''
    });
    setSearchTerm('');
    handleCloseOptions();
};

const validateForm = () => {
    const errors = [];
    if (!formData.name.trim()) errors.push('Department name is required');
    if (!String(formData.status || "").trim()) errors.push('Status is required');

    if (errors.length > 0) {
    showToast("Validation Error", errors.join('\n'), "warning");
    return false;
    }
    return true;
};

const handleDialogClose = () => {
    setOpenAdd(false);
    setOpenView(false);
    setOpenDelete(false);
    setFilterOptions(false);
};

const handleFormDataReset = () => {
    setFormData({
    name: '',
    additional_info: '',
    status: '1'
    });
};

// Export handlers
const handleExportClick = (event) => {
    setExportAnchorEl(event.currentTarget);
};

const handleExportClose = () => {
    setExportAnchorEl(null);
};

const handleExportExcel = async () => {
    try {
    handleExportClose();
    await exportDepartmentsToExcel(filteredData, branchNamesFilterData);
    showToast("Success", "Excel file downloaded successfully", "success");
    } catch (error) {
    console.error('Excel export error:', error);
    showToast("Error", error.message, "error");
    }
};

const handleExportPDF = async () => {
    try {
    handleExportClose();
    await exportDepartmentsToPDFEnhanced(filteredData, branchNamesFilterData, {
        name: filters.name,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        searchTerm: searchTerm
    });
    showToast("Success", "PDF file downloaded successfully", "success");
    } catch (error) {
    console.error('PDF export error:', error);
    showToast("Error", error.message, "error");
    }
};

const handleExportCSV = async () => {
    try {
    handleExportClose();
    await exportDepartmentsToCSV(filteredData, branchNamesFilterData);
    showToast("Success", "CSV file downloaded successfully", "success");
    } catch (error) {
    console.error('CSV export error:', error);
    showToast("Error", error.message, "error");
    }
};

const getSelectedBranchName = () => {
    if (!selectedBranch) return "Select Branch";
    const branch = branchNamesFilterData.find(b => b.id === selectedBranch);
    return branch ? `${branch.name} (${branch.code})` : "Select Branch";
};

const handleSubmit = async (e) => {
    e.preventDefault();
    if (postStatus !== "DELETE") {
        if (!validateForm()) return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append('user_id', getUserDetails("Id"));

    // Set branch_id based on permissions
    if (hasPermission(PERMISSIONS.BRANCHES) && selectedBranch) {
        formDataToSend.append('branch_id', selectedBranch);
    } else {
        formDataToSend.append('branch_id', getUserDetails("branchId"));
    }

    formDataToSend.append('name', formData.name);
    formDataToSend.append('status', formData.status);
    formDataToSend.append('additional_info', formData.additional_info || '');
    formDataToSend.append('user_id', getUserDetails("Id"));


    // Only append updateId for UPDATE/DELETE operations
    if (postStatus === "UPDATE" || postStatus === "DELETE") {
        formDataToSend.append('updateId', updateId);
    }

    try {
        let response;

        if (postStatus === "ADD") {
            response = await departmentApiService.create(formDataToSend);
        } else if (postStatus === "UPDATE") {
            response = await departmentApiService.update(formDataToSend);
        } else if (postStatus === "DELETE") {
            response = await departmentApiService.delete(formDataToSend);
        }
        // Check success based on the service response
        if (response.success) {
            showToast("Success", response.message, "success");
            handleFormDataReset();
            handleDialogClose();
            fetchData();
        } else {
            showToast("Error", response.message, "error");
        }
    } catch (error) {
        showToast("Error", "An unexpected error occurred \n\n" + error.message, "error");
    }
};

if (loading) return <div>Loading...</div>;
if (error) return <div>Error: {error}</div>;

return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
    <Typography sx={{
        fontWeight: 'bold',
        color: theme.palette.primary.main,
        flexShrink: 0,
        width: { xs: '100%', sm: 'auto' },
        mb: { xs: 1, sm: 0 }
    }}>
        Departments Management ( {formatNumberWithComma2(pageState.pageData.length)} Total )
    </Typography>
    
    {/* Display selected branch name */}
    {hasPermission(PERMISSIONS.BRANCHES) && selectedBranch && (
        <Box sx={{ mt: 1, mb: 2 }}>
        <Chip
            label={`Selected Branch: ${getSelectedBranchName()}`}
            color="primary"
            variant="filled"
            onDelete={() => {
            setSelectedBranch('');
            fetchData();
            }}
            deleteIcon={<CloseIcon />}
        />
        </Box>
    )}

    <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        {/* Buttons - column on mobile, row on desktop */}
        <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            flexShrink: 0,
            order: { xs: 2, sm: 1 }
        }}>
            {hasPermission(PERMISSIONS.ADD) && (
            <Button
                variant="contained"
                color="warning"
                startIcon={<AddIcon />}
                onClick={handleOpenAdd}
                sx={{
                textTransform: 'none',
                borderRadius: '8px'
                }}
            >
                Add New Department
            </Button>
            )}

            {hasPermission(PERMISSIONS.BRANCHES) && (
            <Button
                variant="contained"
                color="primary"
                startIcon={<FilterIcon />}
                onClick={() => setBranchSelectionOpen(true)}
                sx={{
                textTransform: 'none',
                borderRadius: '8px'
                }}
            >
                Select Branch
            </Button>
            )}
            
            <Button
            variant="contained"
            color="secondary"
            startIcon={<FilterIcon />}
            onClick={handlefilterOptions}
            sx={{
                textTransform: 'none',
                borderRadius: '8px'
            }}
            >
            View Options
            </Button>
            
            {hasPermission(PERMISSIONS.EXPORT) && (
            <Button
                variant="contained"
                color="primary"
                startIcon={<GetApp />}
                onClick={handleExportClick}
                sx={{
                textTransform: 'none',
                borderRadius: '8px'
                }}
            >
                Export Data
            </Button>
            )}
        </Box>

        {/* Search Bar - full width on mobile, fixed on desktop */}
        <Paper sx={{
            width: { xs: '100%', sm: '300px' },
            p: '2px 8px',
            display: 'flex',
            alignItems: 'center',
            marginLeft: { xs: 0, sm: 'auto' },
            order: { xs: 1, sm: 2 },
            borderRadius: '8px',
            background: theme.palette.primary.light
        }}>
            <TextField
            fullWidth
            variant="outlined"
            placeholder="Search departments..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'white' }} />,
                sx: {
                '& fieldset': { border: 'none' },
                fontSize: '0.875rem',
                color: 'white',
                '& input': {
                    color: 'white',
                    '&::placeholder': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    opacity: 1
                    }
                }
                }
            }}
            size="small"
            />
        </Paper>
        </Box>

        {/* Active Filters as a separate row below */}
        {(filters.name || filters.dateFrom || filters.dateTo) && (
        <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            flexWrap: 'wrap',
            alignItems: 'center',
            mt: 2,
            pt: 2,
            borderTop: '1px solid',
            borderColor: 'divider'
        }}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
            Active filters:
            </Typography>
            {filters.name && (
            <Chip
                label={`Name: ${filters.name}`}
                onDelete={() => setFilters(prev => ({ ...prev, name: '' }))}
                color="primary"
                variant="outlined"
            />
            )}
            {filters.dateFrom && (
            <Chip 
                label={`From: ${new Date(filters.dateFrom).toLocaleDateString()}`}
                onDelete={() => setFilters(prev => ({ ...prev, dateFrom: '' }))}
                color="primary"
                variant="outlined"
            />
            )}
            {filters.dateTo && (
            <Chip 
                label={`To: ${new Date(filters.dateTo).toLocaleDateString()}`}
                onDelete={() => setFilters(prev => ({ ...prev, dateTo: '' }))}
                color="primary"
                variant="outlined"
            />
            )}
            <Button 
            size="small"
            onClick={clearFilters}
            startIcon={<CloseIcon />}
            sx={{ ml: 1 }}
            >
            Clear All
            </Button>
        </Box>
        )}
    </Paper>

    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
    >
        <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <TableContainer>
            <Table>
            <TableHead>
                <TableRow sx={{
                backgroundColor: theme.palette.primary.main,
                '& th': {
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.95rem'
                }
                }}>
                <TableCell>#</TableCell>
                <TableCell>Date Created</TableCell>
                <TableCell>Department Name</TableCell>
                <TableCell>Additional Info</TableCell>
                <TableCell>Status</TableCell>
                {hasPermission(PERMISSIONS.BRANCHES) && <TableCell>Branch</TableCell>}
                <TableCell align="center">Actions</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((department, index) => (
                <TableRow
                    key={index}
                    hover
                    sx={{
                    '&:nth-of-type(odd)': {
                        backgroundColor: theme.palette.action.hover
                    }
                    }}
                >
                    <TableCell>
                    <Chip
                        label={page * rowsPerPage + index + 1}
                        color="primary"
                        size="small"
                    />
                    </TableCell>
                    <TableCell>{formatCreatedAtDate(department.created_at)}</TableCell>
                    <TableCell sx={{ fontWeight: 'medium' }}>
                    {department.name}
                    </TableCell>
                    <TableCell>
                    <Typography variant="body2" sx={{ 
                        maxWidth: '200px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}>
                        {department.additional_info || 'N/A'}
                    </Typography>
                    </TableCell>
                    <TableCell>
                    {department.status === 1 ? (
                        <Chip
                        label={getStatusById(department.status)}
                        size="small"
                        color="primary"
                        />
                    ) : (
                        <Chip
                        label={getStatusById(department.status)}
                        size="small"
                        color="error"
                        />
                    )}
                    </TableCell>
                    {hasPermission(PERMISSIONS.BRANCHES) && (
                    <TableCell>
                        <Chip
                        label={getCategoriesNameById(department.branch_id, branchNamesFilterData)}
                        color="info"
                        variant="outlined"
                        size="small"
                        />
                    </TableCell>
                    )}
                    <TableCell align="center">
                    <IconButton
                        edge="end"
                        onClick={() => handleOpenView(department)}
                        color="primary"
                        sx={{
                        '&:hover': {
                            backgroundColor: theme.palette.info.light
                        }
                        }}
                    >
                        <ViewIcon />
                    </IconButton>
                    {hasPermission(PERMISSIONS.EDIT) && (<>
                        <IconButton
                        edge="end"
                        onClick={() => handleOpenEdit(department)}
                        color="secondary"
                        sx={{
                            '&:hover': {
                            backgroundColor: theme.palette.success.light
                            }
                        }}
                        >
                        <EditIcon />
                        </IconButton>
                        </>)}
                        {hasPermission(PERMISSIONS.DELETE) && (<>
                        <IconButton
                        edge="end"
                        onClick={() => handleOpenDelete(department)}
                        color="error"
                        sx={{
                            '&:hover': {
                            backgroundColor: theme.palette.error.light
                            }
                        }}
                        >
                        <Delete />
                        </IconButton>
                    </>)}
                    </TableCell>
                </TableRow>
                ))}
            </TableBody>
            </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ mt: 2 }}
        />

        {filteredData.length === 0 && (
            <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 8,
            color: 'text.secondary'
            }}>
            <Business sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h6">No departments found</Typography>
            <Typography variant="body2">Try adjusting your search query or add a new department</Typography>
            </Box>
        )}
        </Paper>
    </motion.div>

    {/* Branch Selection Dialog */}
    <Dialog
        open={branchSelectionOpen}
        onClose={() => setBranchSelectionOpen(false)}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="sm"
    >
        <DialogTitle sx={{ textAlign: 'center' }}>Select Branch</DialogTitle>
        <DialogContent>
        <Box sx={{ mt: 2 }}>
            <FormControl fullWidth>
            <InputLabel id="branch-select-label">Select Branch</InputLabel>
            <Select
                labelId="branch-select-label"
                value={selectedBranch}
                label="Select Branch"
                onChange={(e) => handleBranchSelect(e.target.value)}
            >
                <MenuItem value="">
                <em>Select a branch</em>
                </MenuItem>
                {branchNamesFilterData.map((branch) => (
                <MenuItem key={branch.id} value={branch.id}>
                    {branch.name} ({branch.code})
                </MenuItem>
                ))}
            </Select>
            </FormControl>
            
            <Typography variant="body2" sx={{ mt: 3, color: 'text.secondary' }}>
            <strong>Note:</strong> Selecting a branch will filter departments only from that branch. 
            Leave unselected to view all departments.
            </Typography>
        </Box>
        </DialogContent>
        <DialogActions>
        <Button
            color="error"
            onClick={() => setBranchSelectionOpen(false)}
            variant="contained"
        >
            Cancel
        </Button>
        <Button
            onClick={() => {
            if (selectedBranch) {
                fetchData(selectedBranch);
            }
            setBranchSelectionOpen(false);
            }}
            color="primary"
            variant="contained"
        >
            Apply
        </Button>
        </DialogActions>
    </Dialog>

    {/* Export Menu */}
    <Menu
        anchorEl={exportAnchorEl}
        open={Boolean(exportAnchorEl)}
        onClose={handleExportClose}
        anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
        }}
        transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
        }}
    >
        <MenuItem onClick={handleExportExcel} sx={{ gap: 1 }}>
        <TableChart fontSize="small" color="success" />
        Export to Excel (.xlsx)
        </MenuItem>
        <MenuItem onClick={handleExportPDF} sx={{ gap: 1 }}>
        <PictureAsPdf fontSize="small" color="error" />
        Export to PDF (.pdf)
        </MenuItem>
        <MenuItem onClick={handleExportCSV} sx={{ gap: 1 }}>
        <TableChart fontSize="small" color="info" />
        Export to CSV (.csv)
        </MenuItem>
    </Menu>

    {/* Options Dialog */}
    <Dialog
        open={filterOptions}
        onClose={handleCloseOptions}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="sm"
    >
        <DialogTitle sx={{ textAlign: 'center' }}>Filter Options</DialogTitle>
        <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
            <Button
            variant="outlined"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={clearFilters}
            sx={{ textTransform: 'none' }}
            >
            Show All Departments
            </Button>

            <Button
            variant="outlined"
            color="primary"
            startIcon={<TodayIcon />}
            onClick={applyTodayFilter}
            sx={{ textTransform: 'none' }}
            >
            Show Today's Departments
            </Button>

            <Divider>OR</Divider>

            <TextField
            fullWidth
            label="Name"
            name="name"
            value={filters.name}
            onChange={handleFilterChange}
            />

            <Divider>OR</Divider>

            <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
            Filter by Date Range
            </Typography>

            <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
                <TextField
                fullWidth
                label="From Date"
                type="date"
                name="dateFrom"
                value={filters.dateFrom}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                fullWidth
                label="To Date"
                type="date"
                name="dateTo"
                value={filters.dateTo}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
                />
            </Grid>
            </Grid>
        </Box>
        </DialogContent>
        <DialogActions>
        <Button
            color="error"
            onClick={handleCloseOptions}
            variant="contained"
        >
            Close
        </Button>
        <Button
            onClick={handleCloseOptions}
            color="primary"
            variant="contained"
        >
            Apply Filters
        </Button>
        </DialogActions>
    </Dialog>

    {/* Add/Edit Dialog */}
    <Dialog
        open={openAdd}
        onClose={handleCloseAdd}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="sm"
        scroll="paper"
    >
        <DialogTitle sx={{ textAlign: 'center' }}><b>{postStatus === "ADD" ? "Add New Department" : "Edit Department"}</b></DialogTitle>
        <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Box>
            <Grid item xs={12}>
                <TextField
                fullWidth
                label="Department Name"
                name="name"
                value={formData.name}
                autoComplete="off"
                onChange={handleInputChange}
                error={errors.name}
                helperText={errors.name ? "Invalid input" : ""}
                required
                />
            </Grid>
            <br/>
            <Grid item xs={12}>
                <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                    name="status"
                    value={formData.status}
                    autoComplete="off"
                    onChange={handleInputChange}
                    label="Status"
                    required
                >
                    {statusData.map((item, index) => (
                    <MenuItem key={index} value={item.id}>{item.name}</MenuItem>
                    ))}
                </Select>
                </FormControl>
            </Grid>
            <br/>
            <Grid item xs={12}>
                <TextField
                fullWidth
                multiline
                rows={3}
                label="Additional Info"
                name="additional_info"
                value={formData.additional_info}
                autoComplete="off"
                onChange={handleInputChange}
                />
            </Grid>
            </Box>
            <DialogActions sx={{ mt: 3, px: 0 }}>
            <Button
                color="error"
                onClick={handleCloseAdd}
                variant="contained"
            >
                Cancel
            </Button>
            <Button
                type="submit"
                color="primary"
                variant="contained"
            >
                {postStatus === "ADD" ? "Add Department" : "Update Department"}
            </Button>
            </DialogActions>
        </Box>
        </DialogContent>
    </Dialog>

    {/* View Dialog */}
    <Dialog
        open={openView}
        onClose={handleCloseView}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="sm"
    >
        <DialogTitle sx={{ textAlign: 'center' }}><b>Department Details</b></DialogTitle>
        <DialogContent>
        {selectedRecord && (
            <Box sx={{ p: 2 }}>
            <Box>
                <Grid item xs={12}>
                <Typography variant="subtitle2"><b>Name:</b> {selectedRecord.name}</Typography>
                </Grid>
                <br/>
                <Grid item xs={12}>
                <Typography variant="subtitle2">
                    <b>Status:</b>
                    {selectedRecord.status === 1 ? (
                    <Chip
                        label={getStatusById(selectedRecord.status)}
                        size="small"
                        color="primary"
                        sx={{ ml: 1 }}
                    />
                    ) : (
                    <Chip
                        label={getStatusById(selectedRecord.status)}
                        size="small"
                        color="error"
                        sx={{ ml: 1 }}
                    />
                    )}
                </Typography>
                </Grid>
                <br/>
                {hasPermission(PERMISSIONS.BRANCHES) && (
                <Grid item xs={12}>
                    <Typography variant="subtitle2">
                    <b>Branch:</b>
                    <Chip
                        label={getCategoriesNameById(selectedRecord.branch_id, branchNamesFilterData) || 'N/A'}
                        color="info"
                        variant="outlined"
                        size="small"
                        sx={{ ml: 1 }}
                    />
                    </Typography>
                </Grid>
                )}
                <Grid item xs={12}>
                <Typography variant="subtitle2"><b>Additional Info:</b> {selectedRecord.additional_info || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12}>
                <Typography variant="subtitle2"><b>Created Date:</b> {formatCreatedAtDate(selectedRecord.created_at)}</Typography>
                </Grid>
            </Box>
            </Box>
        )}
        </DialogContent>
        <DialogActions>
        <Button
            color="error"
            onClick={handleCloseView}
            variant="contained"
        >
            Close
        </Button>
    </DialogActions>
    </Dialog>

    {/* Delete Confirmation Dialog */}
    <Dialog
    open={openDelete}
    onClose={handleCloseDelete}
    TransitionComponent={Transition}
    >
    <DialogTitle sx={{ textAlign: 'center' }}>Delete Department</DialogTitle>
    <DialogContent>
        <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
        Are you sure you want to delete this department?
        </Typography>
        {selectedRecord && (
        <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {selectedRecord.name}
            </Typography>
            {hasPermission(PERMISSIONS.BRANCHES) && (
            <Typography variant="body2">
                Branch: {getCategoriesNameById(selectedRecord.branch_id, branchNamesFilterData) || 'N/A'}
            </Typography>
            )}
        </Box>
        )}
    </DialogContent>
    <DialogActions>
        <Button
        color="error"
        onClick={handleCloseDelete}
        variant="contained"
        >
        Cancel
        </Button>
        <Button
        onClick={handleSubmit}
        color="primary"
        variant="contained"
        >
        Confirm Delete
        </Button>
    </DialogActions>
    </Dialog>
</Container>
);
};

export default Department;

const Transition = React.forwardRef(function Transition(props, ref) {
return <Slide direction="up" ref={ref} {...props} />;
});



